import { FC, useEffect, useState } from "react";

import {
  serverGetSectionOutcomeSummary,
  serverGetStudentOutcomeCompetency,
} from "api/api-server-data";
import { TermMenu } from "components/menus/TermMenu";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { IconText } from "mds/components/IconText";
import { Menu, MenuDivider, MenuItem } from "mds/components/Menu";
import { ArrowDownIcon, ChevronDownIcon } from "mds/icons";
import { storeApi, useAppSelector } from "store/index";
import {
  selectOutcomeGroups,
  selectOutcomeSubgroups,
  selectOutcomes,
  selectTerms,
} from "store/selectors";
import { toastErrorMessage } from "utils/alerts";
import { BUTTON_EVENT_CATEGORY, trackEvent } from "utils/amplitude";
import { downloadCSVFile } from "utils/file";
import {
  generateSectionOutcomeSummaryCSV,
  generateStudentOutcomeCompetencyCSV,
} from "utils/reporting";

export const StudentLearningOutcomesReport: FC = () => {
  const [selectedTermId, setSelectedTermId] = useState("");
  const terms = useAppSelector(selectTerms);
  const outcomes = useAppSelector(selectOutcomes);
  const outcomeGroups = useAppSelector(selectOutcomeGroups);
  const outcomeSubgroups = useAppSelector(selectOutcomeSubgroups);

  useEffect(() => {
    // Load data into store
    storeApi.outcomes.list();
    storeApi.outcome_groups.list();
    storeApi.outcome_subgroups.list();
  }, []);

  // Convert outcomes to the format expected by CSV generation (already sorted by title)
  const outcomeColumns = outcomes.map((outcome) => ({
    id: outcome.id,
    title: outcome.title,
  }));

  // Convert groups and subgroups to the format expected by CSV generation (already sorted by title)
  const groupColumns = outcomeGroups.map((group) => ({
    id: group.id,
    title: group.title,
  }));

  const subgroupColumns = outcomeSubgroups.map((subgroup) => ({
    id: subgroup.id,
    title: subgroup.title,
  }));

  const handlePerSectionLOAverages = async () => {
    if (!selectedTermId) {
      toastErrorMessage(t("assessment_summary_report.select_term_to_view_data"));
      return;
    }

    try {
      const data = await serverGetSectionOutcomeSummary(selectedTermId);

      if (!data || data.length === 0) {
        toastErrorMessage(t("assessment_summary_report.no_data"));
        return;
      }

      const selectedTerm = terms.find((term) => term.id === selectedTermId);
      const termTitle = selectedTerm?.title || selectedTermId;

      const { csvData, filename } = generateSectionOutcomeSummaryCSV(
        data,
        termTitle,
        outcomeColumns,
      );
      trackEvent("Home - Reports / Download course outcome summary", {
        ...BUTTON_EVENT_CATEGORY,
        fileName: filename,
      });
      downloadCSVFile(csvData, filename);
    } catch {
      toastErrorMessage(t("assessment_summary_report.export_failed"));
    }
  };

  const handleStudentLOCompetencyAverages = async () => {
    try {
      const data = await serverGetStudentOutcomeCompetency(selectedTermId || undefined);

      if (!data || data.length === 0) {
        toastErrorMessage(t("assessment_summary_report.no_data"));
        return;
      }

      const selectedTerm = selectedTermId ? terms.find((term) => term.id === selectedTermId) : null;
      const termTitle = selectedTerm?.title || t("import_modal.all_terms");

      const { csvData, filename } = generateStudentOutcomeCompetencyCSV(
        data,
        termTitle,
        groupColumns,
        subgroupColumns,
        outcomeColumns,
      );
      trackEvent("Home - Reports / Download student outcome competency", {
        ...BUTTON_EVENT_CATEGORY,
        fileName: filename,
      });
      downloadCSVFile(csvData, filename);
    } catch {
      toastErrorMessage(t("assessment_summary_report.export_failed"));
    }
  };

  const MenuButton = (
    <Button className="gap-1" kind="white" size="s">
      <IconText
        iconEnd={<ChevronDownIcon />}
        text={t("student_learning_outcomes_report.menu.title")}
      />
    </Button>
  );

  return (
    <div className="student-learning-outcomes-view">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="h3">{t("student_learning_outcomes_report.title")}</h2>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <TermMenu
            eventPrefix="Home - Reports / "
            menuType="menu"
            selectedTermId={selectedTermId}
            size="s"
            onTermChange={setSelectedTermId}
          />
        </div>

        <div className="flex items-center gap-2">
          <Menu menuButton={MenuButton}>
            <MenuItem
              disabled={!selectedTermId}
              eventName="Student Learning Outcomes Report - Export Per Course LO Averages"
              onClick={handlePerSectionLOAverages}
            >
              <div className="flex items-start gap-2">
                <ArrowDownIcon className="mt-1" />

                <div className="flex flex-col">
                  <div className="font-medium">
                    {t("student_learning_outcomes_report.menu.per_section_lo_averages.title")}
                  </div>

                  <div className="text-xs text-black-tint-40">
                    {t("student_learning_outcomes_report.menu.per_section_lo_averages.description")}
                  </div>
                </div>
              </div>
            </MenuItem>

            <MenuDivider />

            <MenuItem
              eventName="Student Learning Outcomes Report - Export Student LO + Competency Averages"
              onClick={handleStudentLOCompetencyAverages}
            >
              <div className="flex items-start gap-2">
                <ArrowDownIcon className="mt-1" />

                <div className="flex flex-col">
                  <div className="font-medium">
                    {t(
                      "student_learning_outcomes_report.menu.student_lo_competency_averages.title",
                    )}
                  </div>

                  <div className="text-xs text-black-tint-40">
                    {t(
                      "student_learning_outcomes_report.menu.student_lo_competency_averages.description",
                    )}
                  </div>
                </div>
              </div>
            </MenuItem>
          </Menu>
        </div>
      </div>
    </div>
  );
};
